import type { FC } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { FrontofficeDeeplinkResolve200ContentRType } from 'generated/api/frontofficeAPI.schemas';
import { goBack } from 'lib/reduxRouter';

import type { AppState } from 'index';

import useMedia from 'hooks/useMedia';
import { useOfferViewTracking } from 'hooks/useOfferViewTracking';
import { getSameOriginReferrerPath } from 'utils/getSameOriginReferrerPath';
import { getTestIdByParts } from 'utils/getTestIdByParts';
import type { WithTestId } from 'types/app';

import { OfferStateEnum } from '@Modules/offers/constants';
import { redirect } from '@Modules/app/slice';
import { getOfferActions, getOfferTranslation, setShowTranslation } from '@Modules/offers/slice';
import { buildOfferBreadcrumbs } from '@Modules/categories/utils';
import { AnalyticsTypes } from '@Modules/analytics/types';
import { selectIsFirstRendering, selectLanguage, selectRegionUri } from '@Modules/app/selectors';
import {
  selectCategoriesFirstLevel,
  selectCategoriesFlatList,
} from '@Modules/categories/selectors';
import {
  selectOfferCard,
  selectOfferMarkup,
  selectOffersOther,
  selectOffersShowTranslation,
  selectOffersSimilar,
} from '@Modules/offers/selectors';

import { Layout } from '@Atomic/templates/Layout';
import { Page } from '@Atomic/templates/Page';
import NotFound from '@Atomic/pages/NotFound';
import { OffersCarousel } from '@Atomic/organisms/carousels/OffersCarousel';
import { Loan } from '@Atomic/organisms/Loan';
import { AskOfferSeller } from '@Atomic/organisms/offer/AskOfferSeller';
import { ClosedOfferContent } from '@Atomic/organisms/offer/ClosedOfferContent';
import { MainInfo } from '@Atomic/organisms/offer/MainInfo';
import { OfferBid } from '@Atomic/organisms/offer/OfferBid';
import { OfferComplaint } from '@Atomic/organisms/offer/OfferComplaint';
import { Schema } from '@Atomic/organisms/offer/OfferContent/Schema';
import { OfferDescription } from '@Atomic/organisms/offer/OfferDescription';
import { OfferDetails } from '@Atomic/organisms/offer/OfferDetails';
import { OfferFeatures } from '@Atomic/organisms/offer/OfferFeatures';
import { OfferGallery } from '@Atomic/organisms/offer/OfferGallery';
import { OfferLocation } from '@Atomic/organisms/offer/OfferLocation';
import { OfferPhoneModal } from '@Atomic/organisms/offer/OfferPhoneModal';
import { OfferSimilarItems } from '@Atomic/organisms/offer/OfferSimilarItems';
import { OfferTop } from '@Atomic/organisms/offer/OfferTop';
import { SellerDetails } from '@Atomic/organisms/offer/SellerDetails';
import { Col, Container, Row } from '@Atomic/molecules/Grid';
import { OfferCardBanner } from '@Atomic/molecules/OfferCardBanner';
import { MetaCanonical } from '@Atomic/molecules/opengraph/MetaCanonical';
import { Breadcrumbs } from '@Atomic/atoms/Breadcrumbs';
import { Title } from '@Atomic/atoms/Headline';

import styles from './style.module.scss';

export const OfferContent: FC<WithTestId> = ({ testId = 'offer' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { xs, sm } = useMedia();

  const offerError = useSelector((state: AppState) => state.errors[getOfferActions.name]);
  const offerCard = useSelector(selectOfferCard);
  const other = useSelector(selectOffersOther);
  const showTranslation = useSelector(selectOffersShowTranslation);
  const markup = useSelector(selectOfferMarkup);
  const categoriesFlatList = useSelector(selectCategoriesFlatList);
  const firstLevelCategoryIds = useSelector(selectCategoriesFirstLevel);
  const similar = useSelector(selectOffersSimilar);
  const language = useSelector(selectLanguage);
  const region = useSelector(selectRegionUri);
  const isFirstRendering = useSelector(selectIsFirstRendering);

  const anchorRef = useRef<HTMLDivElement | null>(null);

  const isMobile = xs || sm;
  const offer = offerCard?.card;

  useOfferViewTracking(offer?.id);
  const offerExpired = !!offer && offerCard.state !== OfferStateEnum.PUBLISHED;
  const offerTranslation = showTranslation && offerCard ? offerCard.translation : undefined;

  const breadcrumbs = useMemo(() => {
    const breadcrumbsBase = [{ to: '/', label: t('main') }];
    const items = markup?.breadcrumbs
      ? buildOfferBreadcrumbs({
          markupBreadcrumbs: markup.breadcrumbs,
          offerPath: offer?.path ?? [],
          categories: categoriesFlatList,
          firstLevelCategoryIds,
          lang: language,
        })
      : [];

    return [...breadcrumbsBase, ...items, { label: offer?.title }];
  }, [markup, language, t, offer, categoriesFlatList, firstLevelCategoryIds]);

  const handleShowTranslationAction = (value: boolean) => {
    dispatch(setShowTranslation(value));
  };

  useEffect(() => {
    handleShowTranslationAction(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const handleGoBack = () => {
    if (isFirstRendering) {
      // Offers open in a new tab from feeds (favorites, search, etc.), so
      // there is no in-app history — return to the referrer page instead.
      const referrerPath = getSameOriginReferrerPath();

      dispatch(redirect({ to: referrerPath ?? `/${language}/${region}` }));
    } else {
      dispatch(goBack());
    }
  };

  if (offerError) {
    return <NotFound />;
  }

  if (!offer) {
    return null;
  }

  if (offerExpired) {
    return (
      <>
        <Schema offerCard={offerCard} />
        <ClosedOfferContent
          offerCard={offerCard}
          markup={markup}
          breadcrumbs={breadcrumbs}
          onGoBack={handleGoBack}
        />
      </>
    );
  }

  // The backend decides whether the canonical points to the offer itself or to a
  // model filter page (BP-7965); the deeper MetaCanonical overrides the router-level
  // self-canonical the same way the closed-offer override does (BP-7512)
  const canonicalUri = markup?.canonical?.[language];

  const offerSliders = (
    <>
      {!!other.length && (
        <section className={styles.section}>
          <OffersCarousel
            title={t('offer.other')}
            offers={other}
            fullListAction={{
              rType: FrontofficeDeeplinkResolve200ContentRType.SELLER,
              payload: { uuid: offer.seller.uuid },
            }}
            analyticsContext={{ source: AnalyticsTypes.OfferClickSource.SIMILAR }}
            testId={getTestIdByParts(testId, 'other')}
          />
        </section>
      )}

      {!!similar.data.length && (
        <section className={cn(styles.section, styles.similarItems)}>
          <Title.H2 className={styles.sectionTitle}>{t('offer.similar')}</Title.H2>
          <OfferSimilarItems offerId={offer.id} testId={testId} />
        </section>
      )}
    </>
  );

  const topBarAnchor = <div ref={anchorRef} className={styles.anchor} />;

  const description = offerTranslation?.description || offer.description;
  const hasFeatures = !!offer.features.length;
  const hasDescription = !!description?.trim();

  const handleTranslate = () => {
    if (offerCard.translation) {
      handleShowTranslationAction(true);

      return;
    }

    dispatch(
      getOfferTranslation.request(
        { offerId: offer.id },
        { onSuccess: () => handleShowTranslationAction(true) }
      )
    );
  };

  return (
    <Layout
      fixedHeaderOnScroll
      showChatWidget
      hasScrollToTop
      hasSearch
      footerHasOffset
      scrollToTopHasOffset={isMobile}
      fixedAnchorRef={anchorRef}
      fixedContentOnScroll={
        <OfferTop offer={offer} withContacts={!isMobile} onGoBack={handleGoBack} />
      }
    >
      <Schema offerCard={offerCard} />
      {canonicalUri && <MetaCanonical canonical={`/${canonicalUri}`} />}
      <Page
        onGoBack={isMobile ? undefined : handleGoBack}
        breadcrumbs={isMobile ? undefined : breadcrumbs}
      >
        <Container className={styles.offerContent}>
          {isMobile && (
            <>
              {/* Phones get the crumbs in the white header above the compact top, not in Page (BP-8728) */}
              <Breadcrumbs
                className={styles.mobileBreadcrumbs}
                breadcrumbs={breadcrumbs}
                testId="breadcrumbs"
              />
              <OfferTop
                variant="compact"
                offer={offer}
                onGoBack={handleGoBack}
                className={styles.mobileOfferTop}
              />
            </>
          )}

          <Row className={styles.offerRow}>
            <Col
              xs={12}
              sm={12}
              md={6}
              lg={6}
              className={cn(styles.offerColumn, styles.leftColumn)}
            >
              <section className={cn(styles.section, styles.gallery)}>
                <OfferGallery offer={offer} isMobile={isMobile} />

                {isMobile && (
                  <OfferDetails
                    offer={offer}
                    isExpired={false}
                    translation={offerTranslation}
                    variant="no-contacts"
                  />
                )}

                {isMobile && topBarAnchor}
              </section>

              {isMobile && (
                <Loan
                  offerId={offer.id}
                  testId={getTestIdByParts(testId, 'loan')}
                  wrapper={<section className={styles.section} />}
                  price={offer.priceUzs}
                  isBusiness={offer.seller.business}
                />
              )}

              {!!offer.askDiscount?.questions?.length && offer.seller.chatAllowed && (
                <section className={styles.section}>
                  <Title.H2 className={styles.sectionTitle}>{t('offer.offerBid.title')}</Title.H2>
                  <OfferBid
                    key={offer.id}
                    offerId={offer.id}
                    askDiscount={offer.askDiscount}
                    testId={getTestIdByParts(testId, 'offer-bid')}
                  />
                </section>
              )}

              {(hasFeatures || hasDescription) && (
                <section
                  className={styles.section}
                  data-testid={getTestIdByParts(testId, 'details')}
                >
                  <Title.H2 className={cn(styles.sectionTitle, styles.detailsTitle)}>
                    {t('offer.details')}
                  </Title.H2>

                  {hasFeatures && (
                    <OfferFeatures
                      key={offer.id}
                      features={offer.features}
                      testId={getTestIdByParts(testId, 'features')}
                    />
                  )}

                  {hasFeatures && hasDescription && (
                    <hr
                      className={styles.detailsSeparator}
                      data-testid={getTestIdByParts(testId, 'details-separator')}
                    />
                  )}

                  {hasDescription && (
                    <OfferDescription
                      testId={getTestIdByParts(testId, 'description')}
                      translationAvailable={offer.translationAvailable}
                      showTranslation={showTranslation}
                      onShowOriginal={() => handleShowTranslationAction(false)}
                      onTranslate={handleTranslate}
                      description={description}
                    />
                  )}
                </section>
              )}

              {!!offer.askSeller?.questions?.length && offer.seller.chatAllowed && (
                <section className={styles.section}>
                  <Title.H2 className={styles.sectionTitle}>{t('offer.askSeller.title')}</Title.H2>
                  <AskOfferSeller key={offer.id} offerId={offer.id} askSeller={offer.askSeller} />
                </section>
              )}

              {isMobile && (
                <section className={styles.section}>
                  <SellerDetails seller={offer.seller} />
                </section>
              )}

              <section className={styles.section}>
                <Title.H2 className={cn(styles.sectionTitle, styles.placeTitle)}>
                  {t('offer.place')}
                </Title.H2>
                <OfferLocation
                  delivery={offer.delivery}
                  point={offer.location.point}
                  title={offer.location.title}
                  fullAddress={offer.location.fullAddress}
                  courierDelivery={offer.courierDelivery}
                  description={offerTranslation?.locationDescription || offer.location.description}
                  testId={getTestIdByParts(testId, 'delivery')}
                />
              </section>

              {isMobile && (
                <section className={styles.section}>
                  <OfferCardBanner className={styles.banner} />
                </section>
              )}

              {xs && offerSliders}

              <section className={styles.section}>
                <OfferComplaint id={offer.id} publishedAt={offer.publishedAt} testId={testId} />
              </section>

              {sm && offerSliders}
            </Col>

            {!isMobile && (
              <Col
                xs={12}
                sm={12}
                md={6}
                lg={6}
                className={cn(styles.offerColumn, styles.rightColumn)}
              >
                <section className={cn(styles.section, styles.offerInfo)}>
                  <MainInfo
                    testId="offer"
                    offer={offer}
                    translation={offerTranslation}
                    isExpired={false}
                    markup={markup || undefined}
                    onGoBack={handleGoBack}
                  />
                </section>
              </Col>
            )}
          </Row>

          {!isMobile && topBarAnchor}

          {!isMobile && offerSliders}
        </Container>
      </Page>
      <OfferPhoneModal />
    </Layout>
  );
};
